import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import {
  getIndustryLabel,
  getSubIndustryLabel,
  getCollaborationIntentLabel,
  getWhoAmILabel,
  getAdditionalTechInfraLabel,
  FileCategory,
  ProductServiceType,
  regionsData,
} from "@/components/shared/constants/general.const";
import { sanitizeTextForWinAnsiPdf } from "@/utils/pdfWinAnsiText";

const PAGE_MARGIN = 50;
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * PAGE_MARGIN;

const GRAY_TEXT = rgb(0.42, 0.45, 0.51);
const BLACK_TEXT = rgb(0.11, 0.11, 0.11);
const LIGHT_GRAY = rgb(0.96, 0.97, 0.98);
const BORDER_COLOR = rgb(0.898, 0.91, 0.922);

const FS = 9;
const LH = FS * 1.4;
const MIN_ROW_H = 20;
const CELL_PAD = 5;
const BORDER = rgb(0, 0, 0);

function getRegionLabel(regionId: string): string {
  const region = regionsData.regions.find((r) => r.id === regionId);
  return region ? region.name : regionId;
}

function joinLabels<T>(arr: T[] | undefined, map: (x: T) => string): string {
  if (!Array.isArray(arr) || arr.length === 0) return "N/A";
  return arr.map(map).join(", ");
}

/**
 * PDF of the anonymized company preview (same masking as AnonymousProfile / ProfileTab).
 * Layout, header block, logo placement, and footers match the Collaboration Alignment Report style.
 */
export async function downloadAnonymizedProfilePdf(company: Record<string, unknown>): Promise<void> {
  const generatedAt = new Date();
  const pdfDoc = await PDFDocument.create();
  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  let logoImage: Awaited<ReturnType<typeof pdfDoc.embedPng>> | null = null;
  let logoDims = { width: 0, height: 0 };
  try {
    const logoBytes = await fetch("/GT logo.png").then((r) => r.arrayBuffer());
    logoImage = await pdfDoc.embedPng(logoBytes);
    const LOGO_H = 45;
    logoDims = { width: logoImage.width * (LOGO_H / logoImage.height), height: LOGO_H };
  } catch {
    // logo unavailable – skip silently (add GT logo.png to public/ for parity with collaboration PDF)
  }

  let page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  let y = PAGE_HEIGHT - PAGE_MARGIN;

  const drawText = (
    text: string,
    x: number,
    startY: number,
    options: {
      size?: number;
      font?: typeof helvetica;
      color?: typeof BLACK_TEXT;
      maxWidth?: number;
      lineHeight?: number;
    } = {}
  ): number => {
    const {
      size = 10,
      font = helvetica,
      color = BLACK_TEXT,
      maxWidth = CONTENT_WIDTH,
      lineHeight = size * 1.5,
    } = options;

    const safe = sanitizeTextForWinAnsiPdf(text);
    const words = safe.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = font.widthOfTextAtSize(testLine, size);

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) {
      lines.push(currentLine);
    }

    let currentY = startY;
    for (const line of lines) {
      page.drawText(line, { x, y: currentY, size, font, color });
      currentY -= lineHeight;
    }

    return currentY;
  };

  const checkNewPage = (requiredSpace: number) => {
    if (y - requiredSpace < PAGE_MARGIN + 36) {
      page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      y = PAGE_HEIGHT - PAGE_MARGIN;
    }
  };

  const splitLines = (text: string, maxWidth: number, font: typeof helvetica, size: number): string[] => {
    const t = sanitizeTextForWinAnsiPdf(text);
    const words = (t || "N/A").split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let cur = "";
    for (const w of words) {
      const test = cur ? `${cur} ${w}` : w;
      if (font.widthOfTextAtSize(test, size) > maxWidth && cur) {
        lines.push(cur);
        cur = w;
      } else {
        cur = test;
      }
    }
    if (cur) lines.push(cur);
    return lines.length ? lines : ["N/A"];
  };

  const drawTable = (headers: string[], rows: string[][], colWidths: number[]) => {
    checkNewPage(MIN_ROW_H * 3);

    let xOff = PAGE_MARGIN;
    for (let i = 0; i < headers.length; i++) {
      page.drawRectangle({
        x: xOff,
        y: y - MIN_ROW_H,
        width: colWidths[i],
        height: MIN_ROW_H,
        color: LIGHT_GRAY,
        borderColor: BORDER,
        borderWidth: 0.5,
      });
      page.drawText(sanitizeTextForWinAnsiPdf(headers[i]), {
        x: xOff + CELL_PAD,
        y: y - FS - CELL_PAD,
        size: FS,
        font: helveticaBold,
        color: BLACK_TEXT,
      });
      xOff += colWidths[i];
    }
    y -= MIN_ROW_H;

    for (const row of rows) {
      const allLines = row.map((cell, i) => splitLines(cell, colWidths[i] - 2 * CELL_PAD, helvetica, FS));
      const rowH = Math.max(MIN_ROW_H, ...allLines.map((lines) => lines.length * LH + 2 * CELL_PAD));
      checkNewPage(rowH);
      xOff = PAGE_MARGIN;
      for (let i = 0; i < row.length; i++) {
        page.drawRectangle({
          x: xOff,
          y: y - rowH,
          width: colWidths[i],
          height: rowH,
          color: rgb(1, 1, 1),
          borderColor: BORDER,
          borderWidth: 0.5,
        });
        let ty = y - FS - CELL_PAD;
        for (const line of allLines[i]) {
          page.drawText(line, { x: xOff + CELL_PAD, y: ty, size: FS, font: helvetica, color: BLACK_TEXT });
          ty -= LH;
        }
        xOff += colWidths[i];
      }
      y -= rowH;
    }
    y -= 8;
  };

  const drawSectionHeader = (title: string) => {
    checkNewPage(120);
    page.drawRectangle({ x: PAGE_MARGIN, y: y - 30, width: CONTENT_WIDTH, height: 28, color: LIGHT_GRAY });
    page.drawText(sanitizeTextForWinAnsiPdf(title), {
      x: PAGE_MARGIN + 8,
      y: y - 20,
      size: 11,
      font: helveticaBold,
      color: BLACK_TEXT,
    });
    y -= 38;
  };

  const drawBlock = (label: string, value: string) => {
    checkNewPage(56);
    page.drawText(sanitizeTextForWinAnsiPdf(label.toUpperCase()), {
      x: PAGE_MARGIN,
      y,
      size: 8,
      font: helveticaBold,
      color: GRAY_TEXT,
    });
    y -= 12;
    y = drawText(value, PAGE_MARGIN, y, {
      size: 10,
      font: helvetica,
      color: BLACK_TEXT,
      maxWidth: CONTENT_WIDTH,
    });
    y -= 20;
  };

  page.drawText("Anonymized Profile Preview", {
    x: PAGE_MARGIN,
    y,
    size: 18,
    font: helveticaBold,
    color: BLACK_TEXT,
  });
  y -= 20;
  page.drawText("Confidential Report – Generated via the GTsetu Platform", {
    x: PAGE_MARGIN,
    y,
    size: 10,
    font: helveticaBold,
    color: BLACK_TEXT,
  });
  y -= 6;
  page.drawLine({
    start: { x: PAGE_MARGIN, y },
    end: { x: PAGE_WIDTH - PAGE_MARGIN, y },
    thickness: 1,
    color: BORDER_COLOR,
  });
  y -= 20;

  const gtid = company.gtid != null && String(company.gtid) !== "" ? String(company.gtid) : "N/A";

  drawSectionHeader("1. Report overview");
  drawTable(
    ["Item", "Details"],
    [
      ["Report Title", "Anonymized Profile Preview"],
      ["Platform", "GTsetu – Structured Global Collaboration Platform"],
      [
        "Generated at",
        generatedAt.toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" }),
      ],
      [
        "Note",
        "This document mirrors how your company appears to others before a mutual match. Trade name, contact details, and verification fields are masked.",
      ],
    ],
    [160, CONTENT_WIDTH - 160]
  );

  drawSectionHeader("2. Public identity");
  drawBlock("GTID", gtid);
  drawBlock("PROFILE STATUS", company.status != null ? String(company.status) : "N/A");
  drawBlock(
    "HEADQUARTERS (COUNTRY)",
    company.headquartersCountry != null && String(company.headquartersCountry) !== ""
      ? String(company.headquartersCountry)
      : "N/A"
  );
  drawBlock("PHONE (AS SHOWN TO OTHERS)", "XXXX XXXXX");
  drawBlock("EMAIL (AS SHOWN TO OTHERS)", "XXX@mail.com");

  drawSectionHeader("3. Business overview");
  drawBlock(
    "COMPANY DESCRIPTION",
    company.description != null && String(company.description) !== "" ? String(company.description) : "N/A"
  );
  drawBlock("TRADE NAME", "XXXX");
  drawBlock(
    "INDUSTRY CATEGORY",
    joinLabels(company.industryCategory as string[] | undefined, (c) => getIndustryLabel(c))
  );
  drawBlock(
    "SUB INDUSTRY",
    joinLabels(company.subIndustry as string[] | undefined, (s) => getSubIndustryLabel(s))
  );
  drawBlock("WHO AM I", joinLabels(company.whoAmI as string[] | undefined, (r) => getWhoAmILabel(r)));
  drawBlock(
    "COLLABORATION INTENT",
    joinLabels(company.collaborationIntent as string[] | undefined, (i) => getCollaborationIntentLabel(i))
  );

  const productServices = (company.productServices as Array<{ name?: string; description?: string; type: string }>) || [];
  const products = productServices.filter((p) => p.type === ProductServiceType.PRODUCT);
  const services = productServices.filter((p) => p.type === ProductServiceType.SERVICE);

  drawSectionHeader("4. Products & services");
  if (products.length === 0 && services.length === 0) {
    drawBlock("PRODUCTS / SERVICES", "N/A");
  } else {
    if (products.length > 0) {
      drawBlock(
        "PRODUCTS",
        products.map((p) => `${p.name || "Untitled"}: ${p.description || "N/A"}`).join("\n\n")
      );
    }
    if (services.length > 0) {
      drawBlock(
        "SERVICES",
        services.map((p) => `${p.name || "Untitled"}: ${p.description || "N/A"}`).join("\n\n")
      );
    }
  }

  drawSectionHeader("5. Capabilities & offerings");
  const core = (company.coreCapabilities as string[]) || [];
  drawBlock("CORE CAPABILITIES", core.length > 0 ? core.join("  |  ") : "N/A");
  const secondary = (company.secondaryCapabilities as string[]) || [];
  if (secondary.length > 0) {
    drawBlock("SECONDARY CAPABILITIES", secondary.join("  |  "));
  }
  if (company.keyDifferentiators != null && String(company.keyDifferentiators) !== "") {
    drawBlock("KEY DIFFERENTIATORS", String(company.keyDifferentiators));
  }
  const tech = (company.technologies as string[]) || [];
  drawBlock("TECHNOLOGY & INFRASTRUCTURE", tech.length > 0 ? tech.join("  |  ") : "N/A");
  const addTech = (company.additionalTechInfrastructure as string[]) || [];
  if (addTech.length > 0) {
    drawBlock(
      "ADDITIONAL TECHNOLOGY & INFRASTRUCTURE",
      addTech.map((item) => getAdditionalTechInfraLabel(item)).join("  |  ")
    );
  }

  drawSectionHeader("6. Geography & markets");
  const op = (company.operatingCountries as string[]) || [];
  drawBlock("OPERATING GEOGRAPHIES", op.length > 0 ? op.join("  |  ") : "N/A");
  const pref = (company.preferredCollaborationRegions as string[]) || [];
  drawBlock(
    "PREFERRED COLLABORATION REGIONS",
    pref.length > 0 ? pref.map((r) => getRegionLabel(r)).join("  |  ") : "N/A"
  );

  drawSectionHeader("7. Contact person (masked)");
  drawBlock("NAME", "XXXX");
  drawBlock("PHONE NUMBER", "XXXX");
  drawBlock("DESIGNATION", "XXXX");

  const logs = company.externalVerificationLogs as Array<{ metadata?: Record<string, unknown> }> | undefined;
  const metadata = logs?.[0]?.metadata;
  let sectionIndex = 8;
  if (metadata && typeof metadata === "object") {
    drawSectionHeader(`${sectionIndex}. System verification details (masked)`);
    sectionIndex += 1;
    const mask = (v: unknown) => (v != null && String(v) !== "" ? "XXXX" : "-");
    drawBlock("COMPANY NAME", mask(metadata.companyName));
    drawBlock("COMPANY ADDRESS", mask(metadata.companyAddress));
    drawBlock("DATE OF INCORPORATION", mask(metadata.dateOfIncorporation));
    drawBlock("REGISTRATION NUMBER", mask(metadata.registrationNumber));
    drawBlock("COMPANY TYPE", mask(metadata.companyType));
    drawBlock("COMPANY STATUS", mask(metadata.companyStatus));
  }

  const images =
    ((company.images as Array<{ category?: string; filename?: string; originalName?: string }>) || []).filter(
      (f) => f.category === FileCategory.ADDITIONAL_IMAGES
    );
  drawSectionHeader(`${sectionIndex}. Additional images (filenames only)`);
  sectionIndex += 1;
  if (images.length === 0) {
    drawBlock("FILES", "None listed");
  } else {
    drawBlock(
      "FILES",
      images.map((f, i) => `${i + 1}. ${f.originalName || f.filename || "file"}`).join("\n")
    );
  }

  const documents = (company.documents as Array<{ createdAt?: string; uploadedAt?: string }>) || [];
  drawSectionHeader(`${sectionIndex}. Documents (labels as seen by others)`);
  if (documents.length === 0) {
    drawBlock("DOCUMENTS", "No documents available");
  } else {
    drawBlock(
      "DOCUMENTS",
      documents
        .map((d, index) => {
          const raw = d.createdAt || d.uploadedAt;
          const dateStr = raw
            ? new Date(raw).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
            : "";
          return dateStr ? `Document ${index + 1} (uploaded ${dateStr})` : `Document ${index + 1}`;
        })
        .join("\n")
    );
  }

  const pages = pdfDoc.getPages();
  const totalPages = pages.length;
  pages.forEach((p, idx) => {
    if (logoImage) {
      p.drawImage(logoImage, {
        x: PAGE_WIDTH - PAGE_MARGIN - logoDims.width,
        y: PAGE_HEIGHT - PAGE_MARGIN + 5,
        width: logoDims.width,
        height: logoDims.height,
      });
    }
    const pageLabel = `${idx + 1} / ${totalPages}`;
    p.drawText(pageLabel, {
      x: PAGE_WIDTH - PAGE_MARGIN - helvetica.widthOfTextAtSize(pageLabel, 8),
      y: 20,
      size: 8,
      font: helvetica,
      color: GRAY_TEXT,
    });
    p.drawText("GTsetu – Anonymized Profile Preview – Confidential", {
      x: PAGE_MARGIN,
      y: 20,
      size: 7,
      font: helvetica,
      color: GRAY_TEXT,
    });
  });

  const bytes = await pdfDoc.save();
  const blob = new Blob([bytes as unknown as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const stamp = generatedAt.toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const safeGtid = gtid.replace(/[^a-zA-Z0-9-_]/g, "").slice(0, 24) || "profile";
  const a = document.createElement("a");
  a.href = url;
  a.download = `anonymized-profile-${safeGtid}-${stamp}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
